import { Action, createReducer, on } from "@ngrx/store";

import * as CategoryActions from "src/app/store/actions/categories/categories.actions";

import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { CategoryElementDTO } from "src/app/models/Categories/categoryElementDTO";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";

export interface CategoriesState {
  category: CategoryDTO;
  listCategories: CategoryDTO[];
  listCategoryElements: CategoryElementDTO[];
  created: boolean;
  categoryElement: CategoryElementDTO;
  usages: any[];
  isLoading: boolean;
  categoriesTableConfig: ColumnSorterInfo[],
  error: Error;
  totalRecords: number;
  pageReq: PaginationRequest;
  categoriesExcelList?: any[];
  totalCategories?: number;
}

export const initialState: CategoriesState = {
  category: null,
  listCategories: null,
  listCategoryElements: null,
  created: false,
  categoryElement: null,
  usages: null,
  error: null,
  isLoading: false,
  categoriesTableConfig: null,
  pageReq: null,
  totalRecords: null,
  categoriesExcelList: null,
  totalCategories: null,
};

const categoriesReducer = createReducer(
  initialState,
  on(
    CategoryActions.getCategories,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as CategoriesState)
  ),
  on(CategoryActions.loadCategories, (state, { response }) => {
    return {
      ...state,
      listCategories: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      error: null,
      isLoading: false,
      totalCategories: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(
    CategoryActions.getExcelCategories,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as CategoriesState)
  ),
  on(CategoryActions.loadExcelCategories, (state, { response }) => {
    return {
      ...state,
      categoriesExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    CategoryActions.getCategory,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        action: code,
        created: false,
        isLoading: true,
      } as CategoriesState)
  ),
  on(CategoryActions.loadCategory, (state, { response }) => {
    return {
      ...state,
      category: (response as any).result ? (response as any).result : undefined,
      error: null,
      isLoading: false,
    };
  }),
  on(
    CategoryActions.getCategoryUsages,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as CategoriesState)
  ),
  on(CategoryActions.loadCategoryUsages, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      usages: (response as any).result?.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    CategoryActions.getCategoryElements,
    (state, { code }) =>
      ({
        ...state,
        error: null,
        code: code,
      } as CategoriesState)
  ),
  on(CategoryActions.loadCategoryElements, (state, { response }) => {
    return {
      ...state,
      listCategoryElements: (response as any).result?.items || [],
      error: null,
    };
  }),
  on(CategoryActions.getCategoriesTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(CategoryActions.loadCategoriesTableConfig, (state, { response }) => {
    return {
      ...state,
      categoriesTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(CategoryActions.saveCategoriesTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(CategoryActions.setCategory, (state, { category }) => ({
    ...state,
    category: category,
    error: null,
  })),
  on(CategoryActions.resetCategory, (state) => {
    return {
      ...state,
      category: null,
      error: null,
    };
  }),
  on(CategoryActions.setLoadingStartState, (state) => {
    return {
      ...state,
      isLoading: true,
      error: null,
    };
  }),
  on(CategoryActions.setLoadingEndState, (state) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    };
  }),
  on(CategoryActions.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    CategoryActions.createCategory,
    CategoryActions.updateCategory,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }
  )
);

export function reducer(state: CategoriesState | undefined, action: Action) {
  return categoriesReducer(state, action);
}
